import {
  Booking,
  BookingSlots,
  BookingSlotsRequest,
  BookingsRequest,
  Brand,
  CancelBookingRequest,
  CreateBookingRequest,
  CreateBookingResponse,
  CustomerIdentity,
  CustomerIdentityRequest,
  Dealer,
  ServiceType,
  VehicleDetails,
  VehicleDetailsRequest,
  VehicleServiceHistory
} from "../entities/Entities";
import { formatDate, parseDate } from '../../utils/DateUtils';
import { mapObject } from '../../utils/MapUtils';
import {
  BookingDto,
  BookingSlotsDto,
  BookingSlotsRequestDto,
  BookingsRequestDto,
  CancelBookingRequestDto,
  CreateBookingRequestDto,
  CreateBookingResponseDto,
  CustomerIdentityDto,
  CustomerIdentityRequestDto,
  VehicleDetailsDto,
  VehicleDetailsRequestDto,
  VehicleServiceHistoryDto
} from "./BookingApiDtos";
import { useApiMutation, UseApiMutationOptions, UseApiMutationResult } from "../api/UseApiMutation";
import { useApiQuery, UseApiQueryOptions, UseApiQueryResult } from "../api/UseApiQuery";

export function useGetCustomerIdentity(
  request: CustomerIdentityRequest | null,
  options?: UseApiQueryOptions<CustomerIdentity> & { skipGlobalError?: boolean },
): UseApiQueryResult<CustomerIdentity> {
  return useApiQuery(
    ['customer-identity', request?.customerId, request?.passportNumber, request?.registrationNumber],
    (apiClient, _) => apiClient.post<CustomerIdentityRequestDto, CustomerIdentityDto>('/api/v1/booking/customer-identity', mapObject(_)),
    request,
    options,
  );
}

export function useGetVehicleDetails(
  request: VehicleDetailsRequest | null,
  options?: UseApiQueryOptions<VehicleDetails | null> & { skipGlobalError?: boolean },
): UseApiQueryResult<VehicleDetails | null> {
  return useApiQuery(
    ['vehicleDetails', JSON.stringify(request)],
    (apiClient, x) =>
      apiClient.post<VehicleDetailsRequestDto, VehicleDetailsDto | null>('/api/v1/booking/vehicle-details', x).then((data) =>
        data
          ? {
              ...data,
              brand: data.brand as Brand,
              plans: data.plans.map(plan => ({
                ...plan,
                planStartDate: plan.planStartDate === 'No date' ? null : parseDate(plan.planStartDate),
                planEndDate: plan.planEndDate === 'No date' ? null : parseDate(plan.planEndDate),
              }))
          }
          : null,
      ),
    request,
    options,
  );
}

export function useGetVehicleServiceHistory(
  vin: string | null,
  options?: UseApiQueryOptions<VehicleServiceHistory>,
): UseApiQueryResult<VehicleServiceHistory> {
  return useApiQuery(
    ['getVehicleServiceHistory', vin],
    (apiClient, _) =>
      apiClient
        .get<VehicleServiceHistoryDto>('/api/v1/booking/vehicle-service-history', {
          vin: _,
        })
        .then((data) => ({
          ...data,
          serviceDate: parseDate(data.serviceDate),
        })),
    vin,
    options,
  );
}


export function useGetServiceTypes(options?: UseApiQueryOptions<ServiceType[]>): UseApiQueryResult<ServiceType[]> {
  return useApiQuery(['getServiceTypes'], (apiClient) => apiClient.get('/api/v1/booking/service-types'), undefined, options);
}

export function useGetDealers(
  brands: string[],
  options?: UseApiQueryOptions<Dealer[]>,
): UseApiQueryResult<Dealer[]> {
  return useApiQuery(
    ['getDealers', brands],
    (apiClient) => {
      const query = brands.map((b) => `brands=${encodeURIComponent(b)}`).join('&');
      const url = `/api/v1/booking/dealers?${query}`;
      return apiClient.get(url);
    },
    undefined,
    options,
  );
}

export function useGetBookingSlots(
  request: BookingSlotsRequest | null,
  options?: UseApiQueryOptions<BookingSlots>,
): UseApiQueryResult<BookingSlots> {
  return useApiQuery(
    // The window is part of the identity of the result: without the dates a midnight
    // rollover would rebuild the request but keep serving the previous day's cache.
    ['getSlots', request?.dealerCode, request && formatDate(request.startDate)],
    (apiClient, x) =>
      apiClient
        .post<BookingSlotsRequestDto, BookingSlotsDto>('/api/v1/booking/booking-slots', {
          ...x,
          startDate: formatDate(x.startDate),
          endDate: formatDate(x.endDate),
        })
        .then((data) => ({
          bookingSlots: data.bookingSlots.map((slot) => ({
            ...slot,
            date: parseDate(slot.date),
          })),
        })),
    request,
    options,
  );
}

export function useGetBookings(request: BookingsRequest | null, options?: UseApiQueryOptions<Booking[]>): UseApiQueryResult<Booking[]> {
  return useApiQuery(
    ['getBookings', JSON.stringify(request)],
    (apiClient, x) =>
      apiClient.post<BookingsRequestDto, BookingDto[]>('/api/v1/booking/bookings', x).then((response) =>
        response.map((booking) => ({
          ...booking,
          bookingDate: parseDate(booking.bookingDate),
        })),
      ),
    request,
    options,
  );
}

export function useCancelBookingRequest(options?: UseApiMutationOptions<CancelBookingRequest>): UseApiMutationResult<CancelBookingRequest> {
  return useApiMutation(
    (apiClient, request: CancelBookingRequest) =>
      apiClient.post<CancelBookingRequestDto, void>(`/api/v1/booking/bookings/${request.referenceNumber}/cancel`),
    options,
  );
}

export function useCreateBookingRequest(
  request: CreateBookingRequest | null,
  options?: UseApiQueryOptions<CreateBookingResponse> & { skipGlobalError?: boolean },
): UseApiQueryResult<CreateBookingResponse> {
  return useApiQuery(
    ['createBooking', JSON.stringify(request)],
    (apiClient, x) =>
      apiClient.post<CreateBookingRequestDto, CreateBookingResponseDto>('/api/v1/booking/bookings/create', {
        ...x,
        slot: {
          ...x.slot,
          date: formatDate(x.slot.date),
        },
      }),
    request,
    options,
  );
}
